/**
 * CustomToolModal Component
 * Comprehensive modal for creating and editing Custom Tools / External Webhooks
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  AlertTriangle,
  Check,
  Copy,
  Eye,
  EyeOff,
  Lock,
  Play,
  Plus,
  ShieldAlert,
  Sparkles,
  Trash2,
  Wrench,
  X,
} from 'lucide-react';
import {
  Agent,
  CreateToolDefinitionInput,
  ToolDefinition,
  ToolHitlMode,
  ToolPolicies,
  ToolResponsePolicy,
  ToolRiskLevel,
  UserSecret,
} from '../../types';
import {
  createCharacteristic,
  createToolDefinition,
  createUserSecret,
  getToolPolicies,
  listUserSecrets,
  testCallTool,
  updateToolDefinition,
} from '../../services/api';
import { useNotification } from '../../hooks/useNotification';
import { useTranslation } from 'react-i18next';

interface CustomToolModalProps {
  open: boolean;
  onClose: () => void;
  tool?: ToolDefinition | null;
  onSaved: (tool: ToolDefinition) => void;
  agents?: Agent[];
  defaultAgentPublicId?: string;
  initialAgentId?: string;
}

interface ParameterField {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  required: boolean;
  description: string;
}

// Stable reference: an inline `[]` default would change every render and retrigger the populate effect
const EMPTY_AGENTS: Agent[] = [];

export const CustomToolModal: React.FC<CustomToolModalProps> = ({
  open,
  onClose,
  tool,
  onSaved,
  agents = EMPTY_AGENTS,
  defaultAgentPublicId,
  initialAgentId,
}) => {
  const { t } = useTranslation();
  const { success, error: showError } = useNotification();
  const isEdit = Boolean(tool);

  const [activeTab, setActiveTab] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // Policies
  const [policies, setPolicies] = useState<ToolPolicies>({ allowDeleteMethod: false });

  // Secrets list
  const [userSecrets, setUserSecrets] = useState<UserSecret[]>([]);

  // Form State
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [agentPublicId, setAgentPublicId] = useState(defaultAgentPublicId || initialAgentId || '');
  const [endpointUrl, setEndpointUrl] = useState('');
  const [method, setMethod] = useState<'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'>('GET');
  const [timeoutMs, setTimeoutMs] = useState(5000);
  const [status, setStatus] = useState<'active' | 'disabled'>('active');

  // Auth & Security
  const [authType, setAuthType] = useState<string>('none');
  const [selectedSecretId, setSelectedSecretId] = useState<number | ''>('');
  const [newSecretName, setNewSecretName] = useState('');
  const [newSecretValue, setNewSecretValue] = useState('');
  const [showSecretValue, setShowSecretValue] = useState(false);
  const [authHeaderName, setAuthHeaderName] = useState('');
  const [authQueryParamName, setAuthQueryParamName] = useState('');
  const [riskLevel, setRiskLevel] = useState<ToolRiskLevel>('read');
  const [hitlMode, setHitlMode] = useState<ToolHitlMode>('not_required');

  // Parameters Schema
  const [parameters, setParameters] = useState<ParameterField[]>([]);
  const [isAdvancedParamSchema, setIsAdvancedParamSchema] = useState(false);
  const [rawParametersSchema, setRawParametersSchema] = useState(
    '{\n  "type": "object",\n  "properties": {}\n}'
  );

  // Output Schema & Response Policy
  const [allowedJsonPaths, setAllowedJsonPaths] = useState<string[]>([]);
  const [newAllowedPath, setNewAllowedPath] = useState('');
  const [defaultResultPath, setDefaultResultPath] = useState('');
  const [redactPaths, setRedactPaths] = useState<string[]>([]);
  const [newRedactPath, setNewRedactPath] = useState('');
  const [maxItems, setMaxItems] = useState(50);
  const [maxResponseBytes, setMaxResponseBytes] = useState(65536);
  const [isAdvancedResponsePolicy, setIsAdvancedResponsePolicy] = useState(false);
  const [rawResponsePolicy, setRawResponsePolicy] = useState('');

  // Characteristic Guidance
  const [characteristicGuidance, setCharacteristicGuidance] = useState('');
  const [isSavingCharacteristic, setIsSavingCharacteristic] = useState(false);

  // Test Call State
  const [testPayload, setTestPayload] = useState('{}');
  const [isRunningTest, setIsRunningTest] = useState(false);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    status: number;
    latencyMs: number;
    rawResponse: unknown;
    modelPreview: unknown;
  } | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  // Load policies & secrets on open
  useEffect(() => {
    if (open) {
      void getToolPolicies().then((res) => {
        if (res.success && res.data) setPolicies(res.data);
      });
      void listUserSecrets().then((res) => {
        if (res.success && res.data) setUserSecrets(res.data);
      });
    }
  }, [open]);

  // Populate form if editing or opening
  useEffect(() => {
    if (tool) {
      setName(tool.name);
      setDescription(tool.description || '');
      setAgentPublicId(tool.agentPublicId || '');
      setEndpointUrl(tool.endpointUrl);
      setMethod((tool.method.toUpperCase() as any) || 'GET');
      setTimeoutMs(tool.timeoutMs || 5000);
      setStatus(tool.status || 'active');
      setAuthType(tool.authType || 'none');
      setSelectedSecretId(tool.authSecretId || '');
      setAuthHeaderName(tool.authHeaderName || '');
      setAuthQueryParamName(tool.authQueryParamName || '');
      setRiskLevel(tool.riskLevel || 'read');
      setHitlMode(tool.hitlMode || 'not_required');

      // Parameters schema
      if (tool.parametersSchema) {
        setRawParametersSchema(JSON.stringify(tool.parametersSchema, null, 2));
        const props = (tool.parametersSchema as any)?.properties;
        const required = new Set((tool.parametersSchema as any)?.required || []);
        if (props && typeof props === 'object') {
          const list: ParameterField[] = Object.entries(props).map(([k, v]: [string, any]) => ({
            name: k,
            type: v?.type || 'string',
            required: required.has(k),
            description: v?.description || '',
          }));
          setParameters(list);
        } else {
          setParameters([]);
        }
      }

      // Response Policy
      if (tool.responsePolicy) {
        const policy = tool.responsePolicy as ToolResponsePolicy;
        setAllowedJsonPaths(policy.allowedJsonPaths || []);
        setDefaultResultPath(policy.defaultResultPath || '');
        setRedactPaths(policy.redactPaths || []);
        setMaxItems(policy.maxItems || 50);
        setMaxResponseBytes(policy.maxResponseBytes || 65536);
        setRawResponsePolicy(JSON.stringify(tool.responsePolicy, null, 2));
      } else {
        setAllowedJsonPaths([]);
        setDefaultResultPath('');
        setRedactPaths([]);
        setMaxItems(50);
        setMaxResponseBytes(65536);
        setRawResponsePolicy('');
      }

      // Guidance
      setCharacteristicGuidance(
        t(
          'tools.modal.guidanceTemplate',
          'Khi dùng tool "{{name}}":\n- Gọi endpoint {{url}} để lấy dữ liệu thực tế.\n- Không tự bịa thông tin nếu endpoint không trả về kết quả.',
          { name: tool.name, url: tool.endpointUrl }
        )
      );
    } else {
      // Default new tool
      setName('');
      setDescription('');
      setAgentPublicId(defaultAgentPublicId || initialAgentId || agents[0]?.publicId || '');
      setEndpointUrl('https://');
      setMethod('GET');
      setTimeoutMs(5000);
      setStatus('active');
      setAuthType('none');
      setSelectedSecretId('');
      setNewSecretName('');
      setNewSecretValue('');
      setAuthHeaderName('');
      setAuthQueryParamName('');
      setRiskLevel('read');
      setHitlMode('not_required');
      setParameters([]);
      setRawParametersSchema('{\n  "type": "object",\n  "properties": {}\n}');
      setAllowedJsonPaths([]);
      setDefaultResultPath('');
      setRedactPaths([]);
      setMaxItems(50);
      setMaxResponseBytes(65536);
      setRawResponsePolicy('');
      setCharacteristicGuidance('');
      setTestResult(null);
      setTestError(null);
    }
  }, [tool, open, defaultAgentPublicId, agents]);

  // Adjust risk & HITL mode defaults
  const handleMethodChange = (newMethod: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE') => {
    setMethod(newMethod);
    if (newMethod === 'GET') {
      setRiskLevel('read');
      setHitlMode('not_required');
    } else {
      setRiskLevel('write');
      setHitlMode('required');
    }
  };

  const handleRiskChange = (newRisk: ToolRiskLevel) => {
    setRiskLevel(newRisk);
    if (newRisk === 'read') {
      setHitlMode('not_required');
    } else {
      setHitlMode('required');
    }
  };

  // Parameter helpers
  const handleAddParameter = () => {
    setParameters([
      ...parameters,
      { name: `param_${parameters.length + 1}`, type: 'string', required: false, description: '' },
    ]);
  };

  const handleRemoveParameter = (index: number) => {
    setParameters(parameters.filter((_, i) => i !== index));
  };

  const handleUpdateParameter = (index: number, field: keyof ParameterField, value: any) => {
    const updated = [...parameters];
    updated[index] = { ...updated[index], [field]: value };
    setParameters(updated);
  };

  // Compile JSON Schema from parameters array
  const buildJsonSchemaFromParams = (): Record<string, unknown> => {
    if (isAdvancedParamSchema) {
      try {
        return JSON.parse(rawParametersSchema);
      } catch {
        return { type: 'object', properties: {} };
      }
    }

    const properties: Record<string, unknown> = {};
    const required: string[] = [];

    for (const p of parameters) {
      if (p.name.trim()) {
        properties[p.name.trim()] = {
          type: p.type,
          description: p.description.trim() || undefined,
        };
        if (p.required) {
          required.push(p.name.trim());
        }
      }
    }

    return {
      type: 'object',
      properties,
      required: required.length > 0 ? required : undefined,
    };
  };

  // Response Policy compilation
  const buildResponsePolicy = (): ToolResponsePolicy | undefined => {
    if (isAdvancedResponsePolicy && rawResponsePolicy.trim()) {
      try {
        return JSON.parse(rawResponsePolicy);
      } catch {
        return undefined;
      }
    }

    if (
      allowedJsonPaths.length === 0 &&
      !defaultResultPath &&
      redactPaths.length === 0 &&
      maxItems === 50 &&
      maxResponseBytes === 65536
    ) {
      return undefined;
    }

    return {
      modelInputMode: 'raw_json',
      allowedJsonPaths: allowedJsonPaths.length > 0 ? allowedJsonPaths : undefined,
      defaultResultPath: defaultResultPath.trim() || undefined,
      redactPaths: redactPaths.length > 0 ? redactPaths : undefined,
      maxItems,
      maxResponseBytes,
    };
  };

  // Save handler
  const handleSave = async () => {
    if (!name.trim()) {
      showError(t('tools.modal.validation.nameRequired', 'Vui lòng nhập tên công cụ'));
      setActiveTab(0);
      return;
    }
    const trimmedUrl = endpointUrl.trim();
    if (!trimmedUrl || (!trimmedUrl.startsWith('https://') && !trimmedUrl.startsWith('http://'))) {
      showError(
        t(
          'tools.modal.validation.urlRequired',
          'Endpoint URL bắt buộc phải sử dụng giao thức HTTP hoặc HTTPS'
        )
      );
      setActiveTab(0);
      return;
    }

    setIsSaving(true);
    try {
      let authSecretId: number | null =
        typeof selectedSecretId === 'number' ? selectedSecretId : null;

      // If user typed a new secret, create it now securely
      if (authType !== 'none' && !authSecretId && newSecretValue.trim()) {
        const secRes = await createUserSecret(
          newSecretName.trim() || `${name}_secret`,
          newSecretValue.trim(),
          t('tools.modal.secretAutoDescription', 'Tự động tạo cho tool {{name}}', { name })
        );
        if (secRes.success && secRes.data?.id) {
          authSecretId = secRes.data.id;
        } else {
          showError(
            secRes.error || t('tools.modal.errors.saveSecretFailed', 'Lỗi khi lưu mật khẩu an toàn')
          );
          setIsSaving(false);
          return;
        }
      }

      const input: CreateToolDefinitionInput = {
        name: name.trim().toLowerCase().replace(/\s+/g, '_'),
        description: description.trim() || undefined,
        agent_public_id: agentPublicId || undefined,
        endpoint_url: endpointUrl.trim(),
        method,
        auth_type: authType,
        auth_secret_id: authSecretId,
        auth_header_name: authHeaderName.trim() || undefined,
        auth_query_param_name: authQueryParamName.trim() || undefined,
        parameters_schema: buildJsonSchemaFromParams(),
        response_policy: buildResponsePolicy(),
        risk_level: riskLevel,
        hitl_mode: hitlMode,
        requires_confirmation: hitlMode === 'required',
        timeout_ms: timeoutMs,
        status,
      };

      if (isEdit && tool) {
        const res = await updateToolDefinition(tool.publicId, input);
        if (res.success && res.data) {
          success(t('tools.modal.messages.updateSuccess', 'Đã cập nhật công cụ thành công'));
          onSaved(res.data);
          onClose();
        } else {
          showError(res.error || t('tools.modal.errors.updateFailed', 'Lỗi khi cập nhật công cụ'));
        }
      } else {
        const res = await createToolDefinition(input);
        if (res.success && res.data) {
          success(t('tools.modal.messages.createSuccess', 'Đã tạo công cụ thành công'));
          onSaved(res.data);
          onClose();
        } else {
          showError(res.error || t('tools.modal.errors.createFailed', 'Lỗi khi tạo công cụ'));
        }
      }
    } catch (err) {
      showError(
        err instanceof Error
          ? err.message
          : t('tools.modal.errors.systemError', 'Lỗi hệ thống khi lưu công cụ')
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Test Call Runner
  const handleRunTestCall = async () => {
    const trimmedUrl = endpointUrl.trim();
    if (!trimmedUrl || (!trimmedUrl.startsWith('https://') && !trimmedUrl.startsWith('http://'))) {
      showError(
        t(
          'tools.modal.validation.endpointUrlPrefix',
          'Endpoint URL phải bắt đầu bằng http:// hoặc https://'
        )
      );
      return;
    }

    setIsRunningTest(true);
    setTestError(null);
    setTestResult(null);

    try {
      let parsedParams = {};
      try {
        parsedParams = JSON.parse(testPayload);
      } catch {
        parsedParams = {};
      }

      const res = await testCallTool({
        endpoint_url: endpointUrl.trim(),
        method,
        auth_type: authType,
        auth_secret_id: typeof selectedSecretId === 'number' ? selectedSecretId : null,
        auth_header_name: authHeaderName.trim() || undefined,
        auth_query_param_name: authQueryParamName.trim() || undefined,
        raw_secret_value: newSecretValue.trim() || undefined,
        parameters: parsedParams,
        response_policy: buildResponsePolicy(),
      });

      if (res.success && res.data) {
        setTestResult(res.data);
        // Auto suggest characteristic guidance if none
        if (!characteristicGuidance && res.data.modelPreview) {
          const previewKeys = Object.keys((res.data.modelPreview as any) || {});
          setCharacteristicGuidance(
            t(
              'tools.modal.testGuidanceTemplate',
              'Khi dùng {{name}}:\n- Dữ liệu trả về có các trường: {{fields}}.\n- Dựa trên dữ liệu chính xác để trả lời người dùng, không tự suy đoán.',
              { name: name || 'tool', fields: previewKeys.join(', ') }
            )
          );
        }
      } else {
        setTestError(
          res.error ||
            t('tools.modal.errors.testConnectionFailed', 'Lỗi khi thực hiện kiểm tra kết nối')
        );
      }
    } catch (err) {
      setTestError(
        err instanceof Error
          ? err.message
          : t('tools.modal.errors.testCallFailed', 'Kiểm tra kết nối thất bại')
      );
    } finally {
      setIsRunningTest(false);
    }
  };

  // Save Characteristic to Agent
  const handleSaveCharacteristic = async () => {
    if (!agentPublicId) {
      showError(
        t(
          'tools.modal.validation.agentRequiredForGuidance',
          'Vui lòng chọn Agent trước khi gắn Characteristic'
        )
      );
      return;
    }
    if (!characteristicGuidance.trim()) {
      showError(
        t('tools.modal.validation.guidanceRequired', 'Vui lòng nhập nội dung hướng dẫn nghiệp vụ')
      );
      return;
    }

    setIsSavingCharacteristic(true);
    try {
      const res = await createCharacteristic({
        code: `tool_guidance_${name || Date.now()}`,
        name: t('tools.modal.characteristicName', 'Hướng dẫn sử dụng {{name}}', {
          name: name || 'Tool',
        }),
        layer: 'AGENT' as any,
        prompt: characteristicGuidance.trim(),
        status: 'published',
      });

      if (res.success) {
        success(
          t(
            'tools.modal.messages.characteristicSuccess',
            'Đã tạo Characteristic cho Agent thành công'
          )
        );
      } else {
        showError(
          res.error || t('tools.modal.errors.characteristicFailed', 'Lỗi khi tạo Characteristic')
        );
      }
    } catch (err) {
      showError(
        err instanceof Error
          ? err.message
          : t('tools.modal.errors.characteristicSaveError', 'Lỗi lưu characteristic')
      );
    } finally {
      setIsSavingCharacteristic(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: 'bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800',
      }}
    >
      <DialogTitle className="border-b border-gray-200 px-6 py-4 dark:border-slate-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-400">
              <Wrench size={20} />
            </div>
            <div>
              <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
                {isEdit
                  ? t('tools.modal.titleEdit', 'Chỉnh sửa công cụ: {{name}}', { name: tool?.name })
                  : t('tools.modal.titleCreate', 'Tạo Công cụ / Webhook Tùy chỉnh')}
              </Typography>
              <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
                {t(
                  'tools.modal.subtitle',
                  'Kết nối agent với hệ thống Booking, CRM, ERP hoặc API bên ngoài'
                )}
              </Typography>
            </div>
          </div>
          <IconButton size="small" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </IconButton>
        </div>
      </DialogTitle>

      {/* Tabs navigation */}
      <Box className="border-b border-gray-200 bg-gray-50/50 px-6 dark:border-slate-800 dark:bg-slate-900/50">
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          variant="scrollable"
          scrollButtons="auto"
          className="min-h-[44px]"
        >
          <Tab
            label={t('tools.modal.tabs.general', '1. Cấu hình chung')}
            className="text-xs font-semibold normal-case"
          />
          <Tab
            label={t('tools.modal.tabs.authAndRisk', '2. Xác thực & Rủi ro')}
            className="text-xs font-semibold normal-case"
          />
          <Tab
            label={t('tools.modal.tabs.paramsAndPolicy', '3. Tham số & Policy')}
            className="text-xs font-semibold normal-case"
          />
          <Tab
            label={t('tools.modal.tabs.guidance', '4. Hướng dẫn nghiệp vụ')}
            className="text-xs font-semibold normal-case"
          />
          <Tab
            label={t('tools.modal.tabs.testConnection', '5. Thử nghiệm kết nối')}
            className="text-xs font-semibold normal-case"
          />
        </Tabs>
      </Box>

      <DialogContent className="space-y-6 px-6 py-6">
        {/* TAB 0: General Info */}
        {activeTab === 0 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField
                label={t('tools.modal.fields.toolName', 'Tên công cụ (Tool Name)')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t(
                  'tools.modal.fields.toolNamePlaceholder',
                  'vd: check_room_availability'
                )}
                helperText={t(
                  'tools.modal.fields.toolNameHelper',
                  'Chỉ dùng chữ cái thường, số và dấu gạch dưới'
                )}
                fullWidth
                size="small"
                required
              />

              <FormControl fullWidth size="small">
                <InputLabel>{t('tools.modal.fields.assignAgent', 'Gán cho Agent')}</InputLabel>
                <Select
                  value={agentPublicId}
                  label={t('tools.modal.fields.assignAgent', 'Gán cho Agent')}
                  onChange={(e) => setAgentPublicId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>{t('tools.modal.fields.allAgents', 'Tất cả Agent của tôi')}</em>
                  </MenuItem>
                  {agents.map((ag) => (
                    <MenuItem key={ag.publicId} value={ag.publicId}>
                      {ag.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {t(
                    'tools.modal.fields.assignAgentHelper',
                    'Agent được phép nhận biết và gọi công cụ này'
                  )}
                </FormHelperText>
              </FormControl>
            </div>

            <TextField
              label={t('tools.modal.fields.description', 'Mô tả chức năng (Description)')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t(
                'tools.modal.fields.descriptionPlaceholder',
                'vd: Tra cứu tình trạng phòng trống và bảng giá theo ngày từ hệ thống khách sạn.'
              )}
              multiline
              rows={2}
              fullWidth
              size="small"
              helperText={t(
                'tools.modal.fields.descriptionHelper',
                'Mô tả ngắn gọn để Agent hiểu khi nào cần gọi công cụ này'
              )}
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <FormControl size="small">
                <InputLabel>{t('tools.modal.fields.method', 'Phương thức (Method)')}</InputLabel>
                <Select
                  value={method}
                  label={t('tools.modal.fields.method', 'Phương thức (Method)')}
                  onChange={(e) => handleMethodChange(e.target.value as any)}
                >
                  <MenuItem value="GET">
                    {t('tools.modal.methods.get', 'GET (Tra cứu / Chỉ đọc)')}
                  </MenuItem>
                  <MenuItem value="POST">
                    {t('tools.modal.methods.post', 'POST (Tạo mới dữ liệu)')}
                  </MenuItem>
                  <MenuItem value="PUT">
                    {t('tools.modal.methods.put', 'PUT (Cập nhật toàn bộ)')}
                  </MenuItem>
                  <MenuItem value="PATCH">
                    {t('tools.modal.methods.patch', 'PATCH (Cập nhật một phần)')}
                  </MenuItem>
                  <MenuItem value="DELETE" disabled={!policies.allowDeleteMethod}>
                    DELETE{' '}
                    {policies.allowDeleteMethod
                      ? t('tools.modal.methods.deleteAllowed', '(Được phép)')
                      : t('tools.modal.methods.deleteLocked', '(Bị khóa bởi Admin)')}
                  </MenuItem>
                </Select>
              </FormControl>

              <div className="sm:col-span-2">
                <TextField
                  label={t('tools.modal.fields.endpointUrl', 'Endpoint URL')}
                  value={endpointUrl}
                  onChange={(e) => setEndpointUrl(e.target.value)}
                  placeholder="https://api.yourhotel.com/v1/rooms hoặc http://localhost:8080/api"
                  helperText={t(
                    'tools.modal.fields.endpointUrlHelper',
                    'Hỗ trợ HTTPS hoặc HTTP (cho phép localhost/IP nội bộ khi phát triển)'
                  )}
                  fullWidth
                  size="small"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField
                label={t('tools.modal.fields.timeout', 'Timeout (ms)')}
                type="number"
                value={timeoutMs}
                onChange={(e) => setTimeoutMs(Number(e.target.value))}
                helperText={t(
                  'tools.modal.fields.timeoutHelper',
                  'Thời gian chờ tối đa (từ 1,000ms đến 10,000ms)'
                )}
                fullWidth
                size="small"
              />

              <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-2 dark:border-slate-800">
                <div>
                  <Typography
                    variant="body2"
                    className="font-semibold text-gray-900 dark:text-slate-100"
                  >
                    {t('tools.modal.fields.enableTool', 'Kích hoạt công cụ')}
                  </Typography>
                  <Typography variant="caption" className="text-gray-500">
                    {t(
                      'tools.modal.fields.enableToolHelper',
                      'Bật để Agent bắt đầu sử dụng công cụ'
                    )}
                  </Typography>
                </div>
                <Switch
                  checked={status === 'active'}
                  onChange={(e) => setStatus(e.target.checked ? 'active' : 'disabled')}
                  color="primary"
                />
              </div>
            </div>
          </div>
        )}

        {/* TAB 1: Auth & Risk Level */}
        {activeTab === 1 && (
          <div className="space-y-5">
            {/* Risk Level Setting */}
            <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
              <Typography
                variant="subtitle2"
                className="mb-2 font-semibold text-gray-900 dark:text-slate-100"
              >
                {t('tools.modal.risk.title', 'Phân loại mức độ Rủi ro (Risk Level)')}
              </Typography>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div
                  onClick={() => handleRiskChange('read')}
                  className={`cursor-pointer rounded-lg border p-3 transition ${
                    riskLevel === 'read'
                      ? 'border-blue-500 bg-blue-50/80 dark:bg-blue-950/40'
                      : 'border-gray-200 hover:border-gray-300 dark:border-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-blue-700 dark:text-blue-400">
                    <Check size={16} /> {t('tools.modal.risk.readTitle', 'Read (Chỉ đọc)')}
                  </div>
                  <p className="mt-1 text-xs text-gray-600 dark:text-slate-400">
                    {t(
                      'tools.modal.risk.readDesc',
                      'Tự động thực thi trong chat, không làm thay đổi trạng thái bên ngoài.'
                    )}
                  </p>
                </div>

                <div
                  onClick={() => handleRiskChange('write')}
                  className={`cursor-pointer rounded-lg border p-3 transition ${
                    riskLevel === 'write'
                      ? 'border-amber-500 bg-amber-50/80 dark:bg-amber-950/40'
                      : 'border-gray-200 hover:border-gray-300 dark:border-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-amber-700 dark:text-amber-400">
                    <AlertTriangle size={16} />{' '}
                    {t('tools.modal.risk.writeTitle', 'Write (Ghi dữ liệu)')}
                  </div>
                  <p className="mt-1 text-xs text-gray-600 dark:text-slate-400">
                    {t(
                      'tools.modal.risk.writeDesc',
                      'Tạo booking, đặt lịch, sửa dữ liệu. Có thể cấu hình bắt người dùng xác nhận.'
                    )}
                  </p>
                </div>

                <div
                  onClick={() => handleRiskChange('sensitive')}
                  className={`cursor-pointer rounded-lg border p-3 transition ${
                    riskLevel === 'sensitive'
                      ? 'border-red-500 bg-red-50/80 dark:bg-red-950/40'
                      : 'border-gray-200 hover:border-gray-300 dark:border-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-red-700 dark:text-red-400">
                    <ShieldAlert size={16} />{' '}
                    {t('tools.modal.risk.sensitiveTitle', 'Sensitive (Nhạy cảm)')}
                  </div>
                  <p className="mt-1 text-xs text-gray-600 dark:text-slate-400">
                    {t(
                      'tools.modal.risk.sensitiveDesc',
                      'Thanh toán, hoàn tiền, thao tác tài chính. BẮT BUỘC người dùng bấm xác nhận.'
                    )}
                  </p>
                </div>
              </div>

              {/* HITL Switch for Write action */}
              {riskLevel === 'write' && (
                <div className="mt-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-900/50 dark:bg-amber-950/20">
                  <div>
                    <Typography
                      variant="body2"
                      className="font-semibold text-gray-900 dark:text-slate-100"
                    >
                      {t('tools.modal.hitl.title', 'Cơ chế phê duyệt của con người (HITL)')}
                    </Typography>
                    <Typography variant="caption" className="text-gray-600 dark:text-slate-400">
                      {hitlMode === 'required'
                        ? t(
                            'tools.modal.hitl.requiredDesc',
                            'Bắt buộc: Agent sẽ hiển thị thẻ xác nhận và đợi người dùng bấm Xác nhận trước khi gọi API.'
                          )
                        : t(
                            'tools.modal.hitl.bypassDesc',
                            'Bypass có điều kiện: Agent được phép tự động gọi API kèm Idempotency Key chống trùng lặp.'
                          )}
                    </Typography>
                  </div>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={hitlMode === 'required'}
                        onChange={(e) =>
                          setHitlMode(e.target.checked ? 'required' : 'owner_bypass_allowed')
                        }
                        color="warning"
                      />
                    }
                    label={
                      <span className="text-xs font-semibold">
                        {hitlMode === 'required'
                          ? t('tools.modal.hitl.requiredLabel', 'Bắt xác nhận')
                          : t('tools.modal.hitl.bypassLabel', 'Bypass')}
                      </span>
                    }
                  />
                </div>
              )}
            </div>

            {/* Authentication Settings */}
            <div className="rounded-xl border border-gray-200 p-4 dark:border-slate-800">
              <Typography
                variant="subtitle2"
                className="mb-3 font-semibold text-gray-900 dark:text-slate-100"
              >
                {t('tools.modal.auth.title', 'Xác thực cuộc gọi (Authentication)')}
              </Typography>

              <FormControl fullWidth size="small" className="mb-4">
                <InputLabel>{t('tools.modal.auth.typeLabel', 'Kiểu xác thực')}</InputLabel>
                <Select
                  value={authType}
                  label={t('tools.modal.auth.typeLabel', 'Kiểu xác thực')}
                  onChange={(e) => setAuthType(e.target.value)}
                >
                  <MenuItem value="none">
                    {t('tools.modal.auth.none', 'Không xác thực (Công khai)')}
                  </MenuItem>
                  <MenuItem value="bearer_token">
                    {t('tools.modal.auth.bearerToken', 'Bearer Token (Authorization: Bearer ...)')}
                  </MenuItem>
                  <MenuItem value="api_key_header">
                    {t(
                      'tools.modal.auth.apiKeyHeader',
                      'API Key trong Header (vd: X-API-Key: ...)'
                    )}
                  </MenuItem>
                  <MenuItem value="api_key_query">
                    {t(
                      'tools.modal.auth.apiKeyQuery',
                      'API Key trong Query Params (vd: ?api_key=...)'
                    )}
                  </MenuItem>
                  <MenuItem value="hmac_sha256">
                    {t('tools.modal.auth.hmacSha256', 'HMAC SHA-256 Signature (Chữ ký điện tử)')}
                  </MenuItem>
                </Select>
              </FormControl>

              {authType === 'api_key_header' && (
                <TextField
                  label={t('tools.modal.auth.headerName', 'Tên Header chứa API Key')}
                  value={authHeaderName}
                  onChange={(e) => setAuthHeaderName(e.target.value)}
                  placeholder="X-API-Key hoặc X-Auth-Token"
                  fullWidth
                  size="small"
                  className="mb-4"
                />
              )}

              {authType === 'api_key_query' && (
                <TextField
                  label={t('tools.modal.auth.queryParamName', 'Tên Query Parameter')}
                  value={authQueryParamName}
                  onChange={(e) => setAuthQueryParamName(e.target.value)}
                  placeholder="api_key hoặc token"
                  fullWidth
                  size="small"
                  className="mb-4"
                />
              )}

              {authType !== 'none' && (
                <div className="space-y-3 rounded-lg bg-gray-50 p-3.5 dark:bg-slate-800/60">
                  <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-slate-400">
                    <Lock size={14} className="text-teal-600" />
                    <span>
                      {t(
                        'tools.modal.auth.encryptionNotice',
                        'Secret được mã hóa an toàn bằng AES-GCM 256-bit trên Cloudflare Web Crypto. Không lưu trữ dạng văn bản thô.'
                      )}
                    </span>
                  </div>

                  {userSecrets.length > 0 && (
                    <FormControl fullWidth size="small">
                      <InputLabel>
                        {t('tools.modal.auth.selectSavedSecret', 'Chọn Secret đã lưu')}
                      </InputLabel>
                      <Select
                        value={selectedSecretId}
                        label={t('tools.modal.auth.selectSavedSecret', 'Chọn Secret đã lưu')}
                        onChange={(e) => setSelectedSecretId(e.target.value as any)}
                      >
                        <MenuItem value="">
                          <em>
                            {t('tools.modal.auth.enterNewSecretBelow', 'Nhập Secret mới bên dưới')}
                          </em>
                        </MenuItem>
                        {userSecrets.map((sec) => (
                          <MenuItem key={sec.id} value={sec.id}>
                            {sec.name} ({t('tools.modal.auth.createdAt', 'Tạo ngày')}{' '}
                            {new Date(sec.createdAt).toLocaleDateString()})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {!selectedSecretId && (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <TextField
                        label={t('tools.modal.auth.secretNameLabel', 'Tên gợi nhớ Secret')}
                        value={newSecretName}
                        onChange={(e) => setNewSecretName(e.target.value)}
                        placeholder={t(
                          'tools.modal.auth.secretNamePlaceholder',
                          'vd: Nailsmap API Key'
                        )}
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label={t(
                          'tools.modal.auth.secretValueLabel',
                          'Giá trị Secret (Mật mã / Token)'
                        )}
                        type={showSecretValue ? 'text' : 'password'}
                        value={newSecretValue}
                        onChange={(e) => setNewSecretValue(e.target.value)}
                        placeholder={t(
                          'tools.modal.auth.secretValuePlaceholder',
                          'Nhập token bí mật...'
                        )}
                        size="small"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => setShowSecretValue(!showSecretValue)}
                              >
                                {showSecretValue ? <EyeOff size={16} /> : <Eye size={16} />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: Parameters & Response Policy */}
        {activeTab === 2 && (
          <div className="space-y-6">
            {/* Parameters Schema Builder */}
            <div className="rounded-xl border border-gray-200 p-4 dark:border-slate-800">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <Typography
                    variant="subtitle2"
                    className="font-semibold text-gray-900 dark:text-slate-100"
                  >
                    {t('tools.modal.params.title', 'Tham số truyền vào (Parameters Schema)')}
                  </Typography>
                  <Typography variant="caption" className="text-gray-500">
                    {t(
                      'tools.modal.params.subtitle',
                      'Định nghĩa các tham số mà Agent có thể trích xuất và truyền vào endpoint'
                    )}
                  </Typography>
                </div>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setIsAdvancedParamSchema(!isAdvancedParamSchema)}
                  className="text-xs"
                >
                  {isAdvancedParamSchema
                    ? t('tools.modal.params.visualMode', 'Chế độ Trực quan')
                    : t('tools.modal.params.jsonMode', 'JSON Schema Editor')}
                </Button>
              </div>

              {isAdvancedParamSchema ? (
                <TextField
                  multiline
                  rows={6}
                  value={rawParametersSchema}
                  onChange={(e) => setRawParametersSchema(e.target.value)}
                  fullWidth
                  className="font-mono text-xs"
                  placeholder="JSON Schema..."
                />
              ) : (
                <div className="space-y-2.5">
                  {parameters.length === 0 && (
                    <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center text-xs text-gray-500 dark:border-slate-700">
                      {t(
                        'tools.modal.params.empty',
                        'Chưa có tham số nào. Nhấn "Thêm tham số" bên dưới để bổ sung (ví dụ: serviceId, district, limit).'
                      )}
                    </div>
                  )}

                  {parameters.map((p, idx) => (
                    <div
                      key={idx}
                      className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50/50 p-2.5 dark:border-slate-800 dark:bg-slate-800/40"
                    >
                      <TextField
                        size="small"
                        label={t('tools.modal.params.paramName', 'Tên tham số')}
                        value={p.name}
                        onChange={(e) => handleUpdateParameter(idx, 'name', e.target.value)}
                        placeholder="serviceId"
                        className="w-36"
                      />
                      <FormControl size="small" className="w-28">
                        <Select
                          value={p.type}
                          onChange={(e) =>
                            handleUpdateParameter(idx, 'type', e.target.value as any)
                          }
                        >
                          <MenuItem value="string">string</MenuItem>
                          <MenuItem value="number">number</MenuItem>
                          <MenuItem value="boolean">boolean</MenuItem>
                          <MenuItem value="object">object</MenuItem>
                          <MenuItem value="array">array</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label={t('tools.modal.params.aiDescription', 'Mô tả cho AI')}
                        value={p.description}
                        onChange={(e) => handleUpdateParameter(idx, 'description', e.target.value)}
                        placeholder={t(
                          'tools.modal.params.aiDescriptionPlaceholder',
                          'ID dịch vụ cần tra cứu'
                        )}
                        className="min-w-[150px] flex-1"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={p.required}
                            onChange={(e) =>
                              handleUpdateParameter(idx, 'required', e.target.checked)
                            }
                          />
                        }
                        label={
                          <span className="text-xs">
                            {t('tools.modal.params.required', 'Bắt buộc')}
                          </span>
                        }
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveParameter(idx)}
                        className="text-red-500"
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </div>
                  ))}

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Plus size={14} />}
                    onClick={handleAddParameter}
                    className="mt-2 text-xs"
                  >
                    {t('tools.modal.params.addParam', 'Thêm tham số')}
                  </Button>
                </div>
              )}
            </div>

            {/* Response Policy */}
            <div className="rounded-xl border border-gray-200 p-4 dark:border-slate-800">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <Typography
                    variant="subtitle2"
                    className="font-semibold text-gray-900 dark:text-slate-100"
                  >
                    {t(
                      'tools.modal.responsePolicy.title',
                      'Chính sách Xử lý Kết quả (Response Policy)'
                    )}
                  </Typography>
                  <Typography variant="caption" className="text-gray-500">
                    {t(
                      'tools.modal.responsePolicy.subtitle',
                      'Lọc JSONPath an toàn, ẩn trường nhạy cảm và giới hạn kích thước trước khi đưa vào Model'
                    )}
                  </Typography>
                </div>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setIsAdvancedResponsePolicy(!isAdvancedResponsePolicy)}
                  className="text-xs"
                >
                  {isAdvancedResponsePolicy
                    ? t('tools.modal.responsePolicy.simpleForm', 'Form đơn giản')
                    : t('tools.modal.responsePolicy.jsonEditor', 'JSON Policy Editor')}
                </Button>
              </div>

              {isAdvancedResponsePolicy ? (
                <TextField
                  multiline
                  rows={6}
                  value={rawResponsePolicy}
                  onChange={(e) => setRawResponsePolicy(e.target.value)}
                  placeholder='{"allowedJsonPaths": ["$.items[*]"], "maxItems": 50}'
                  fullWidth
                  className="font-mono text-xs"
                />
              ) : (
                <div className="space-y-4">
                  <TextField
                    label={t(
                      'tools.modal.responsePolicy.defaultResultPath',
                      'Đường dẫn trích xuất chính (Default Result Path)'
                    )}
                    value={defaultResultPath}
                    onChange={(e) => setDefaultResultPath(e.target.value)}
                    placeholder="vd: $.items hoặc $.data"
                    size="small"
                    fullWidth
                    helperText={t(
                      'tools.modal.responsePolicy.defaultResultPathHelper',
                      'Nếu response bọc trong { data: [...] }, điền $.data để tự bóc tách mảng kết quả'
                    )}
                  />

                  {/* Allowed JSONPaths */}
                  <div>
                    <Typography
                      variant="caption"
                      className="font-semibold text-gray-700 dark:text-slate-300"
                    >
                      {t(
                        'tools.modal.responsePolicy.allowedJsonPaths',
                        'Cho phép các đường dẫn JSONPath (Allowed JSONPaths)'
                      )}
                    </Typography>
                    <div className="mt-1.5 flex gap-2">
                      <TextField
                        size="small"
                        value={newAllowedPath}
                        onChange={(e) => setNewAllowedPath(e.target.value)}
                        placeholder="vd: $.items[*] hoặc $.meta"
                        className="flex-1"
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          if (newAllowedPath.trim()) {
                            setAllowedJsonPaths([...allowedJsonPaths, newAllowedPath.trim()]);
                            setNewAllowedPath('');
                          }
                        }}
                      >
                        {t('tools.modal.responsePolicy.addBtn', 'Thêm')}
                      </Button>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {allowedJsonPaths.map((p, i) => (
                        <Chip
                          key={i}
                          label={p}
                          size="small"
                          onDelete={() =>
                            setAllowedJsonPaths(allowedJsonPaths.filter((_, idx) => idx !== i))
                          }
                        />
                      ))}
                    </div>
                  </div>

                  {/* Redact Paths */}
                  <div>
                    <Typography
                      variant="caption"
                      className="font-semibold text-gray-700 dark:text-slate-300"
                    >
                      {t(
                        'tools.modal.responsePolicy.redactPaths',
                        'Ẩn các trường nhạy cảm (Redact Paths)'
                      )}
                    </Typography>
                    <div className="mt-1.5 flex gap-2">
                      <TextField
                        size="small"
                        value={newRedactPath}
                        onChange={(e) => setNewRedactPath(e.target.value)}
                        placeholder="vd: $.items[*].internalNote hoặc $.token"
                        className="flex-1"
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          if (newRedactPath.trim()) {
                            setRedactPaths([...redactPaths, newRedactPath.trim()]);
                            setNewRedactPath('');
                          }
                        }}
                      >
                        {t('tools.modal.responsePolicy.addBtn', 'Thêm')}
                      </Button>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {redactPaths.map((p, i) => (
                        <Chip
                          key={i}
                          label={p}
                          size="small"
                          color="warning"
                          onDelete={() => setRedactPaths(redactPaths.filter((_, idx) => idx !== i))}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <TextField
                      label={t(
                        'tools.modal.responsePolicy.maxItems',
                        'Giới hạn số phần tử (Max Items)'
                      )}
                      type="number"
                      value={maxItems}
                      onChange={(e) => setMaxItems(Number(e.target.value))}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label={t(
                        'tools.modal.responsePolicy.maxResponseBytes',
                        'Giới hạn kích thước phản hồi (Max Bytes)'
                      )}
                      type="number"
                      value={maxResponseBytes}
                      onChange={(e) => setMaxResponseBytes(Number(e.target.value))}
                      size="small"
                      fullWidth
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 3: Characteristic Guidance */}
        {activeTab === 3 && (
          <div className="space-y-4">
            <Alert severity="info" className="text-xs">
              <strong>Characteristic</strong>{' '}
              {t(
                'tools.modal.characteristic.alertText',
                'cung cấp chỉ dẫn nghiệp vụ để AI hiểu ý nghĩa các trường dữ liệu (ví dụ: items[].price là giá dịch vụ, items[].salonName là tên salon), từ đó so sánh và trả lời chính xác mà không cần code mapping phức tạp.'
              )}
            </Alert>

            <TextField
              label={t(
                'tools.modal.characteristic.guidanceLabel',
                'Hướng dẫn nghiệp vụ cho Model (Characteristic Guidance)'
              )}
              multiline
              rows={8}
              value={characteristicGuidance}
              onChange={(e) => setCharacteristicGuidance(e.target.value)}
              placeholder={t(
                'tools.modal.characteristic.guidancePlaceholder',
                'Khi dùng {{name}}:\n- Response chứa danh sách trong field items.\n- Mỗi item có salonName là tên salon, price là giá, currency là tiền tệ.\n- Khi user hỏi salon rẻ nhất, hãy chọn item có price thấp nhất trong cùng dịch vụ/khu vực.\n- Không tự bịa giá, tình trạng phòng nếu API không trả về.',
                { name: name || 'tên_tool' }
              )}
              fullWidth
              helperText={t(
                'tools.modal.characteristic.guidanceHelper',
                'Nội dung này sẽ được gắn vào System Prompt của Agent'
              )}
            />

            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outlined"
                startIcon={<Copy size={16} />}
                onClick={() => {
                  void navigator.clipboard.writeText(characteristicGuidance);
                  success(t('tools.modal.characteristic.copied', 'Đã sao chép vào bộ nhớ tạm'));
                }}
              >
                {t('tools.modal.characteristic.copyBtn', 'Sao chép văn bản')}
              </Button>

              <Button
                variant="contained"
                color="secondary"
                startIcon={
                  isSavingCharacteristic ? <CircularProgress size={16} /> : <Sparkles size={16} />
                }
                onClick={handleSaveCharacteristic}
                disabled={isSavingCharacteristic || !characteristicGuidance.trim()}
              >
                {t('tools.modal.characteristic.saveBtn', 'Tạo Characteristic cho Agent')}
              </Button>
            </div>
          </div>
        )}

        {/* TAB 4: Test Connection */}
        {activeTab === 4 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Typography
                  variant="subtitle2"
                  className="font-semibold text-gray-900 dark:text-slate-100"
                >
                  {t(
                    'tools.modal.test.title',
                    'Thử nghiệm gọi kết nối (Test Connection / Test Call)'
                  )}
                </Typography>
                <Typography variant="caption" className="text-gray-500">
                  {t(
                    'tools.modal.test.subtitle',
                    'Gửi thử nghiệm request từ máy chủ an toàn với SSRF Guard và kiểm tra xem Model sẽ nhận được dữ liệu gì'
                  )}
                </Typography>
              </div>
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  isRunningTest ? (
                    <CircularProgress size={16} className="text-white" />
                  ) : (
                    <Play size={16} />
                  )
                }
                onClick={handleRunTestCall}
                disabled={isRunningTest}
              >
                {t('tools.modal.test.runBtn', 'Chạy thử (Test Run)')}
              </Button>
            </div>

            <TextField
              label={t('tools.modal.test.payloadLabel', 'Tham số thử nghiệm (Test Payload JSON)')}
              multiline
              rows={3}
              value={testPayload}
              onChange={(e) => setTestPayload(e.target.value)}
              fullWidth
              className="font-mono text-xs"
              placeholder='{"serviceId": "haircut_01", "district": "Quan 1"}'
            />

            {testError && (
              <Alert severity="error" className="text-xs">
                {testError}
              </Alert>
            )}

            {testResult && (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Chip
                    label={`Status: ${testResult.status}`}
                    color={testResult.ok ? 'success' : 'error'}
                    size="small"
                  />
                  <span className="text-xs text-gray-500">
                    {t('tools.modal.test.latency', 'Thời gian phản hồi:')} {testResult.latencyMs}ms
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Typography
                      variant="caption"
                      className="font-semibold text-gray-700 dark:text-slate-300"
                    >
                      {t('tools.modal.test.rawResponse', 'Phản hồi gốc từ API (Đã ẩn mật khẩu):')}
                    </Typography>
                    <pre className="mt-1 max-h-56 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-xs dark:border-slate-800 dark:bg-slate-900">
                      {JSON.stringify(testResult.rawResponse, null, 2)}
                    </pre>
                  </div>

                  <div>
                    <Typography
                      variant="caption"
                      className="font-semibold text-indigo-700 dark:text-indigo-400"
                    >
                      {t(
                        'tools.modal.test.modelPreview',
                        'Dữ liệu Model sẽ nhận được sau Response Policy:'
                      )}
                    </Typography>
                    <pre className="mt-1 max-h-56 overflow-auto rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 font-mono text-xs dark:border-indigo-900/50 dark:bg-slate-900">
                      {JSON.stringify(testResult.modelPreview, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>

      <DialogActions className="border-t border-gray-200 px-6 py-3.5 dark:border-slate-800">
        <Button onClick={onClose} disabled={isSaving}>
          {t('common.cancel', 'Hủy')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          className="bg-indigo-600 text-white hover:bg-indigo-700"
        >
          {isSaving ? (
            <CircularProgress size={20} className="text-white" />
          ) : isEdit ? (
            t('tools.modal.updateBtn', 'Cập nhật Công cụ')
          ) : (
            t('tools.modal.createBtn', 'Tạo Công cụ')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
